import { VdsField } from '@volt/volt-extended-components-react';
import VdsBasicInputSafe from '@cas/components/vds-basic-input-safe/VdsBasicInputSafe';
import { FieldValues, UseControllerProps, useController } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { useAppSelector } from '@casops/store/hooks';
import { useTranslation } from 'react-i18next';
import styles from './CasFormDateTimePicker.module.scss';

export interface CasFormDateTimePickerProps<TFormValues extends FieldValues>
  extends UseControllerProps<TFormValues> {
  label?: string;
  isReadonly?: boolean;
}

export function CasFormDateTimePicker<TFormValues extends FieldValues>({
  label,
  ...props
}: CasFormDateTimePickerProps<TFormValues>) {
  const { t } = useTranslation();
  const { disabled, ...sanitisedProps } = props;
  const { field, fieldState, formState } = useController(sanitisedProps);
  const { timezone } = useAppSelector(state => state.settings.localization) || 'UTC';
  const [localDate, setLocalDate] = useState('');
  const [localTime, setLocalTime] = useState('');

  const isError = fieldState.error !== undefined;
  const errorMessage = isError ? fieldState.error?.message : undefined;
  const isDisabled = props.disabled || field.disabled || formState.isSubmitting;

  const onInputChange = (date: string, time: string) => {
    if (date && time) {
      const dt = DateTime.fromISO(`${date}T${time}`, { zone: timezone });
      if (dt.isValid) {
        field.onChange(dt.toUTC().toISO({ suppressMilliseconds: true }));
      }
    }
  };

  const onDateChange = (event: CustomEvent<string>) => {
    // handle clear button
    if (!event.detail) {
      field.onChange('');
    }

    const date = event.detail;
    setLocalDate(date);
    onInputChange(date, localTime);
  };

  const onTimeChange = (event: CustomEvent<string>) => {
    const time = event.detail;
    setLocalTime(time);
    onInputChange(localDate, time);
  };

  useEffect(() => {
    // initialize to now in user's timezone
    const now = DateTime.now().setZone(timezone);
    const date = now.toISODate() ?? '';
    const time = now.toFormat('HH:mm');

    setLocalDate(date);
    setLocalTime(time);
    onInputChange(date, time);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toVdsFieldProps = (props: CasFormDateTimePickerProps<TFormValues>) => {
    return {
      isDisabled: isDisabled,
    };
  };

  const toVdsInputProps = (props: CasFormDateTimePickerProps<TFormValues>) => {
    return {
      disabled: isDisabled,
      isReadonly: props.isReadonly,
      onBlur: field.onBlur,
    };
  };

  return (
    <VdsField key={props.name} label={label ?? ''} isDisabled={isDisabled}>
      <div className={styles.wrapper}>
        <div className={styles.dateTimeWrapper}>
          <div className={styles.date}>
            <VdsField
              label={t('Fields.ActivationDate')}
              errorMessage={errorMessage}
              {...toVdsFieldProps(props)}
            >
              <VdsBasicInputSafe
                {...toVdsInputProps(props)}
                type="date"
                value={localDate}
                error={fieldState.error !== undefined}
                onChangeInput={onDateChange}
              ></VdsBasicInputSafe>
            </VdsField>
          </div>
          <div className={styles.time}>
            <VdsField label={t('Fields.ActivationTime')} {...toVdsFieldProps(props)}>
              <VdsBasicInputSafe
                {...toVdsInputProps(props)}
                type="time"
                value={localTime}
                onChangeInput={onTimeChange}
              ></VdsBasicInputSafe>
            </VdsField>
          </div>
        </div>
      </div>
    </VdsField>
  );
}
